// Import actions
import * as UserActions from '../../../actions/userActions';
import * as BusinessOrganisationActions from '../../../actions/businessOrganisationActions';
import * as DocumentTemplateActions from '../../../actions/documentTemplateActions';
import * as ApplicationUserActions from '../../../actions/applicationUserActions';
import * as PatientActions from '../../../actions/patientActions';
import * as CohortActions from '../../../actions/cohortActions';
import * as SubjectActions from '../../../actions/subjectActions';
import * as LabResultActions from '../../../actions/labResultActions';
import * as LocationActions from '../../../actions/locationActions';
import * as UploadBatchActions from '../../../actions/uploadBatchActions';
import * as PortalPostActions from '../../../actions/portalPostActions';
import * as TemplateActions from '../../../actions/templateActions';
import * as IdentifierVaultActions from '../../../actions/identifierVaultActions';

export const fieldEntityActionConfig = {
    BusinessOrganisationIds: {
        fieldName: "BusinessOrganisationIds",
        primarySearchProp: "name",
        secondarySearchProp: "contactEmail",
        singularEntity: "businessOrganisation",
        pluralEntity: "businessOrganisations",
        constantPrefix: "BUSINESS_ORGANISATION",
        summaryProps: ['name', 'contactEmail', 'contactPhone', 'subscriptionPackage'],
        loadDto: BusinessOrganisationActions.loadBusinessOrganisationDto,
        createEntity: BusinessOrganisationActions.createBusinessOrganisation,
        updateEntity: BusinessOrganisationActions.updateBusinessOrganisation,
        deleteEntity: BusinessOrganisationActions.deleteBusinessOrganisation,
        getEntityDetails: BusinessOrganisationActions.getBusinessOrganisationDetails,
        listEntities: BusinessOrganisationActions.listBusinessOrganisations,
        listEntitiesByIds: BusinessOrganisationActions.listBusinessOrganisationsByIds,
        enableSearchBar: true,
    },

    IdentityUserId: {
        fieldName: "IdentityUserId",
        primarySearchProp: "userName",
        secondarySearchProp: "email",
        singularEntity: "user",
        pluralEntity: "users",
        constantPrefix: "USER",
        summaryProps: ['userName', 'email', 'phoneNumber', 'isActive'],
        listEntities: UserActions.listUsersInOrganisation,
        enableSearchBar: false
    },

    DocumentTemplateId: {
        fieldName: "DocumentTemplateId",
        primarySearchProp: "name",
        secondarySearchProp: "type",
        singularEntity: "documentTemplate",
        pluralEntity: "documentTemplates",
        constantPrefix: "DOCUMENT_TEMPLATE",
        summaryProps: ['name', 'type', 'description', 'status'],
        loadDto: DocumentTemplateActions.loadDocumentTemplateDto,
        createEntity: DocumentTemplateActions.createDocumentTemplate,
        updateEntity: DocumentTemplateActions.updateDocumentTemplate,
        deleteEntity: DocumentTemplateActions.deleteDocumentTemplate,
        getEntityDetails: DocumentTemplateActions.getDocumentTemplateDetails,
        listEntities: DocumentTemplateActions.listDocumentTemplates,
        listEntitiesByIds: DocumentTemplateActions.listDocumentTemplatesByIds,
        enableSearchBar: true,
    },

    DocumentTemplateIds: {
        fieldName: "DocumentTemplateIds",
        primarySearchProp: "name",
        secondarySearchProp: "type",
        singularEntity: "documentTemplate",
        pluralEntity: "documentTemplates",
        constantPrefix: "DOCUMENT_TEMPLATE",
        summaryProps: ['name', 'type', 'description', 'status'],
        loadDto: DocumentTemplateActions.loadDocumentTemplateDto,
        createEntity: DocumentTemplateActions.createDocumentTemplate,
        updateEntity: DocumentTemplateActions.updateDocumentTemplate,
        deleteEntity: DocumentTemplateActions.deleteDocumentTemplate,
        getEntityDetails: DocumentTemplateActions.getDocumentTemplateDetails,
        listEntities: DocumentTemplateActions.listDocumentTemplates,
        listEntitiesByIds: DocumentTemplateActions.listDocumentTemplatesByIds,
        enableSearchBar: true,
    },

    ApplicationUserIds: {
        fieldName: "ApplicationUserIds",
        primarySearchProp: "name",
        secondarySearchProp: "email",
        singularEntity: "applicationUser",
        pluralEntity: "applicationUsers",
        constantPrefix: "PRACTITIONER",
        summaryProps: ['name', 'email', 'phoneNumber', 'isActive'],
        loadDto: ApplicationUserActions.loadApplicationUserDto,
        createEntity: ApplicationUserActions.createApplicationUser,
        updateEntity: ApplicationUserActions.updateApplicationUser,
        deleteEntity: ApplicationUserActions.deleteApplicationUser,
        getEntityDetails: ApplicationUserActions.getApplicationUserDetails,
        listEntities: ApplicationUserActions.listApplicationUsers,
        listEntitiesByIds: ApplicationUserActions.listApplicationUsersByIds,
        enableSearchBar: true
    },

    PatientId: {
        fieldName: "PatientId",
        primarySearchProp: "patientIdentifier",
        secondarySearchProp: "nhsNumber",
        singularEntity: "patient",
        pluralEntity: "patients",
        constantPrefix: "PATIENT",
        summaryProps: ['patientIdentifier', 'nhsNumber', 'gender', 'email', 'phoneNumber', 'isActive'],
        loadDto: PatientActions.loadPatientDto,
        createEntity: PatientActions.createPatient,
        updateEntity: PatientActions.updatePatient,
        deleteEntity: PatientActions.deletePatient,
        getEntityDetails: PatientActions.getPatientDetails,
        listEntities: PatientActions.listPatients,
        listEntitiesByIds: PatientActions.listPatientsByIds
    },

    PatientIds: {
        fieldName: "PatientIds",
        primarySearchProp: "patientIdentifier",
        secondarySearchProp: "nhsNumber",
        singularEntity: "patient",
        pluralEntity: "patients",
        constantPrefix: "PATIENT",
        summaryProps: ['patientIdentifier', 'nhsNumber', 'gender', 'email', 'phoneNumber', 'isActive'],
        loadDto: PatientActions.loadPatientDto,
        createEntity: PatientActions.createPatient,
        updateEntity: PatientActions.updatePatient,
        deleteEntity: PatientActions.deletePatient,
        getEntityDetails: PatientActions.getPatientDetails,
        listEntities: PatientActions.listPatients,
        listEntitiesByIds: PatientActions.listPatientsByIds
    },

    // Deliberately no createEntity and no deleteEntity: vault participants are minted by
    // VaultPatientRepository.ResolveAsync when a Patient is saved, and protocol §5.3 forbids
    // deleting them. Their absence is what removes the Create and Delete buttons —
    // DynamicFormSP and EntityManagementScreen both render those only when the action exists.
    VaultPatientIds: {
        fieldName: "VaultPatientIds",
        primarySearchProp: "patientIdentifier",
        secondarySearchProp: "nhsNumber",
        singularEntity: "vaultPatient",
        pluralEntity: "vaultPatients",
        singularEntityLabelKey: "Participant",
        pluralEntityLabelKey: "Participants",
        constantPrefix: "VAULT_PATIENT",
        summaryProps: ['patientIdentifier', 'studyHash', 'nhsNumber', 'chiNumber', 'dateOfBirth', 'isOptedIn', 'isOptedOut'],
        loadDto: IdentifierVaultActions.loadVaultPatientDto,
        updateEntity: IdentifierVaultActions.updateVaultPatient,
        getEntityDetails: IdentifierVaultActions.getVaultPatientDetails,
        listEntities: IdentifierVaultActions.listVaultPatients,
        listEntitiesByIds: IdentifierVaultActions.listVaultPatientsByIds,
        enableSearchBar: true,
        enableCreate: false,
    },

    VaultSubStudyIds: {
        fieldName: "VaultSubStudyIds",
        primarySearchProp: "futureStudyCode",
        secondarySearchProp: "createdAt",
        singularEntity: "vaultSubStudy",
        pluralEntity: "vaultSubStudies",
        singularEntityLabelKey: "Sub-study",
        pluralEntityLabelKey: "Sub-studies",
        constantPrefix: "VAULT_SUB_STUDY",
        summaryProps: ['futureStudyCode', 'description', 'createdAt'],
        loadDto: IdentifierVaultActions.loadVaultSubStudyDto,
        createEntity: IdentifierVaultActions.createVaultSubStudy,
        updateEntity: IdentifierVaultActions.updateVaultSubStudy,
        deleteEntity: IdentifierVaultActions.deleteVaultSubStudy,
        getEntityDetails: IdentifierVaultActions.getVaultSubStudyDetails,
        listEntities: IdentifierVaultActions.listVaultSubStudies,
        listEntitiesByIds: IdentifierVaultActions.listVaultSubStudiesByIds,
        enableSearchBar: true,
    },

    CohortId: {
        fieldName: "CohortId",
        primarySearchProp: "cohortName",
        secondarySearchProp: "description",
        singularEntity: "cohort",
        pluralEntity: "cohorts",
        singularEntityLabelKey: "cohort",
        pluralEntityLabelKey: "cohorts",
        constantPrefix: "COHORT",
        summaryProps: ['cohortName', 'description', 'status', 'principalInvestigator', 'ethicsApprovalReference'],
        loadDto: CohortActions.loadCohortDto,
        createEntity: CohortActions.createCohort,
        updateEntity: CohortActions.updateCohort,
        deleteEntity: CohortActions.deleteCohort,
        getEntityDetails: CohortActions.getCohortDetails,
        listEntities: CohortActions.listCohorts,
        listEntitiesByIds: CohortActions.listCohortsByIds,
        enableSearchBar: true
    },

    CohortIds: {
        fieldName: "CohortIds",
        primarySearchProp: "cohortName",
        secondarySearchProp: "description",
        singularEntity: "cohort",
        pluralEntity: "cohorts",
        singularEntityLabelKey: "cohort",
        pluralEntityLabelKey: "cohorts",
        constantPrefix: "COHORT",
        summaryProps: ['cohortName', 'description', 'status', 'principalInvestigator', 'ethicsApprovalReference'],
        loadDto: CohortActions.loadCohortDto,
        createEntity: CohortActions.createCohort,
        updateEntity: CohortActions.updateCohort,
        deleteEntity: CohortActions.deleteCohort,
        getEntityDetails: CohortActions.getCohortDetails,
        listEntities: CohortActions.listCohorts,
        listEntitiesByIds: CohortActions.listCohortsByIds,
        enableSearchBar: true
    },

    UploadBatchId: {
        fieldName: "UploadBatchId",
        primarySearchProp: "fileName",
        secondarySearchProp: "fileFormat",
        singularEntity: "uploadBatch",
        pluralEntity: "uploadBatches",
        singularEntityLabelKey: "uploadBatch",
        pluralEntityLabelKey: "uploadBatches",
        constantPrefix: "UPLOAD_BATCH",
        summaryProps: ['fileName', 'fileFormat', 'status', 'totalRecords', 'processedRecords'],
        loadDto: UploadBatchActions.loadUploadBatchDto,
        createEntity: UploadBatchActions.createUploadBatch,
        updateEntity: UploadBatchActions.updateUploadBatch,
        deleteEntity: UploadBatchActions.deleteUploadBatch,
        getEntityDetails: UploadBatchActions.getUploadBatchDetails,
        listEntities: UploadBatchActions.listUploadBatches,
        listEntitiesByIds: UploadBatchActions.listUploadBatchesByIds,
        enableSearchBar: true
    },

    UploadBatchIds: {
        fieldName: "UploadBatchIds",
        primarySearchProp: "fileName",
        secondarySearchProp: "fileFormat",
        singularEntity: "uploadBatch",
        pluralEntity: "uploadBatches",
        singularEntityLabelKey: "uploadBatch",
        pluralEntityLabelKey: "uploadBatches",
        constantPrefix: "UPLOAD_BATCH",
        summaryProps: ['fileName', 'fileFormat', 'status', 'totalRecords', 'processedRecords', 'id'],
        loadDto: UploadBatchActions.loadUploadBatchDto,
        createEntity: UploadBatchActions.createUploadBatch,
        updateEntity: UploadBatchActions.updateUploadBatch,
        deleteEntity: UploadBatchActions.deleteUploadBatch,
        getEntityDetails: UploadBatchActions.getUploadBatchDetails,
        listEntities: UploadBatchActions.listUploadBatches,
        listEntitiesByIds: UploadBatchActions.listUploadBatchesByIds,
        enableSearchBar: true
    },

    SubjectIds: {
        fieldName: "SubjectIds",
        primarySearchProp: "subjectIdentifier",
        secondarySearchProp: "postcode",
        singularEntity: "subject",
        pluralEntity: "subjects",
        constantPrefix: "SUBJECT",
        summaryProps: ['subjectIdentifier', 'patientId', 'postcode', 'status', 'enrollmentStatus', 'approvalStatus'],
        loadDto: SubjectActions.loadSubjectDto,
        createEntity: SubjectActions.createSubject,
        updateEntity: SubjectActions.updateSubject,
        deleteEntity: SubjectActions.deleteSubject,
        getEntityDetails: SubjectActions.getSubjectDetails,
        listEntities: SubjectActions.listSubjects,
        listEntitiesByIds: SubjectActions.listSubjectsByIds,
        enableSearchBar: true
    },

    SubjectId: {
        fieldName: "SubjectId",
        primarySearchProp: "subjectIdentifier",
        secondarySearchProp: "postcode",
        singularEntity: "subject",
        pluralEntity: "subjects",
        constantPrefix: "SUBJECT",
        summaryProps: ['subjectIdentifier', 'patientId', 'postcode', 'status', 'enrollmentStatus', 'approvalStatus'],
        loadDto: SubjectActions.loadSubjectDto,
        createEntity: SubjectActions.createSubject,
        updateEntity: SubjectActions.updateSubject,
        deleteEntity: SubjectActions.deleteSubject,
        getEntityDetails: SubjectActions.getSubjectDetails,
        listEntities: SubjectActions.listSubjects,
        listEntitiesByIds: SubjectActions.listSubjectsByIds,
        enableSearchBar: true
    },

    // LabResultIds: 
    LabResultIds: {
        fieldName: "LabResultIds",
        primarySearchProp: "testName",
        secondarySearchProp: "testCode",
        singularEntity: "labResult",
        pluralEntity: "labResults",
        constantPrefix: "LAB_RESULT",
        summaryProps: ['labResultIdentifier', 'testCode', 'testName', 'status'],
        loadDto: LabResultActions.loadLabResultDto,
        createEntity: LabResultActions.createLabResult,
        updateEntity: LabResultActions.updateLabResult,
        deleteEntity: LabResultActions.deleteLabResult,
        getEntityDetails: LabResultActions.getLabResultDetails,
        listEntities: LabResultActions.listLabResults,
        listEntitiesByIds: LabResultActions.listLabResultsByIds,
        enableSearchBar: true
    },

    LocationId: {
        fieldName: "LocationId",
        primarySearchProp: "name",
        secondarySearchProp: "locationCode",
        singularEntity: "location",
        pluralEntity: "locations",
        constantPrefix: "LOCATION",
        summaryProps: ['name', 'locationCode', 'odsCode', 'postcode', 'city', 'status', 'isInApprovedScope'],
        loadDto: LocationActions.loadLocationDto,
        createEntity: LocationActions.createLocation,
        updateEntity: LocationActions.updateLocation,
        deleteEntity: LocationActions.deleteLocation,
        getEntityDetails: LocationActions.getLocationDetails,
        listEntities: LocationActions.listLocations,
        listEntitiesByIds: LocationActions.listLocationsByIds,
        enableSearchBar: true
    },

    LocationIds: {
        fieldName: "LocationIds",
        primarySearchProp: "name",
        secondarySearchProp: "locationCode",
        singularEntity: "location",
        pluralEntity: "locations",
        constantPrefix: "LOCATION",
        summaryProps: ['name', 'locationCode', 'odsCode', 'postcode', 'city', 'status', 'isInApprovedScope'],
        loadDto: LocationActions.loadLocationDto,
        createEntity: LocationActions.createLocation,
        updateEntity: LocationActions.updateLocation,
        deleteEntity: LocationActions.deleteLocation,
        getEntityDetails: LocationActions.getLocationDetails,
        listEntities: LocationActions.listLocations,
        listEntitiesByIds: LocationActions.listLocationsByIds,
        enableSearchBar: true
    },

    PortalPostIds: {
        fieldName: "PortalPostIds",
        primarySearchProp: "title",
        secondarySearchProp: "slug",
        singularEntity: "portalPost",
        pluralEntity: "portalPosts",
        singularEntityLabelKey: "Portal Post",
        pluralEntityLabelKey: "Portal Posts",
        constantPrefix: "PORTAL_POST",
        summaryProps: ['title', 'slug', 'status', 'categoryName', 'authorName', 'publishedAt', 'updatedAt'],
        loadDto: PortalPostActions.loadPortalPostDto,
        createEntity: PortalPostActions.createPortalPostEntity,
        updateEntity: PortalPostActions.updatePortalPostEntity,
        deleteEntity: PortalPostActions.deletePortalPostEntity,
        getEntityDetails: PortalPostActions.getPortalPostDetails,
        listEntities: PortalPostActions.listPortalPosts,
        listEntitiesByIds: PortalPostActions.listPortalPostsByIds,
        enableSearchBar: true,
    },

    TemplateIds: {
        fieldName: "TemplateIds",
        primarySearchProp: "Name",
        secondarySearchProp: "Type",
        singularEntity: "systemTemplate",
        pluralEntity: "systemTemplates",
        constantPrefix: "SYSTEM_TEMPLATE",
        summaryProps: ['name', 'description', 'type', 'category', 'isActive'],
        loadDto: TemplateActions.loadTemplateDto,
        createEntity: TemplateActions.createSystemTemplate,
        updateEntity: TemplateActions.updateSystemTemplate,
        deleteEntity: TemplateActions.deleteSystemTemplate,
        getEntityDetails: TemplateActions.getSystemTemplateDetails,
        listEntities: (filter = {}) =>
            TemplateActions.listSystemTemplates(
                filter.page ?? 1,
                filter.query ?? '',
                filter.limit ?? 10,
            ),
        listEntitiesByIds: TemplateActions.listTemplatesByIds,
        enableSearchBar: true,
    },
};